"use client"

import { useEffect, useRef, useState } from "react"
import { Config } from "@/config"
import { useSocket } from "@/providers/socket-provider"
import { useAppSelector } from "@/redux/hooks"
import { TOrder } from "@/types"
import { debug, roles } from "@/utils"
import { debounce } from "lodash"

import { NewOrderModal } from "../shared/dashboard/orders-management/order-status/new-order-modal"
import { Modal } from "./modal"

interface NewOrderResponse {
  message: string
  orderDetails: TOrder
}

const allowedRoles = [roles.ADMIN, roles.DEV, roles.SUPER_ADMIN]

export function NewOrderSoundModal() {
  const auth = useAppSelector((state) => state.auth)
  const token = auth.token
  const user = auth.user
  const role = user?.role

  const isAdmin = allowedRoles.includes(role as roles)

  const { socket, isConnected } = useSocket()
  const [open, setOpen] = useState<boolean>(false)
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [orderData, setOrderData] = useState<TOrder | null>(null)

  const audioContextRef = useRef<AudioContext | null>(null)
  const audioBufferRef = useRef<AudioBuffer | null>(null)
  const sourceRef = useRef<AudioBufferSourceNode | null>(null)

  const debouncedOrderHandler = debounce((data: NewOrderResponse) => {
    setOrderData(data.orderDetails)
    setOpen(true)
    setIsLoading(false)
    playSound()
  }, 200)

  useEffect(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!socket || !isConnected || !token || !isAdmin) {
      return
    }

    audioContextRef.current = new AudioContext()

    const loadAudio = async () => {
      try {
        const response = await fetch("/sound/order-sound.mp3")
        if (!response.ok) {
          throw new Error(`Failed to fetch audio: ${response.status}`)
        }
        const arrayBuffer = await response.arrayBuffer()
        audioBufferRef.current =
          await audioContextRef.current!.decodeAudioData(arrayBuffer)
      } catch (error) {
        debug("Error loading audio", { level: "error", data: error })
        setError("Failed to load notification sound")
      }
    }
    loadAudio()

    const handleFirstInteraction = () => {
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        audioContextRef.current.resume().then(() => {
          debug("AudioContext resumed", { level: "info" })
        })
      }
      document.removeEventListener("click", handleFirstInteraction)
    }
    document.addEventListener("click", handleFirstInteraction)

    const orderHandler = (data: NewOrderResponse) => {
      debouncedOrderHandler(data)
    }

    const errorHandler = (error: { message: string }) => {
      setError(error.message)
      setIsLoading(false)
    }

    socket.on("newOrder", orderHandler)
    socket.on("error", errorHandler)

    return () => {
      socket.off("newOrder", orderHandler)
      socket.off("error", errorHandler)
      debouncedOrderHandler.cancel()
      document.removeEventListener("click", handleFirstInteraction)
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [socket, isConnected, token, isAdmin])

  useEffect(() => {
    if (!open && sourceRef.current) {
      sourceRef.current.stop()
      sourceRef.current = null
    }
  }, [open])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const playSound = () => {
    if (!audioContextRef.current || !audioBufferRef.current) {
      debug("Audio not ready: context or buffer missing", { level: "error" })
      return
    }
    debug("AudioContext state", {
      level: "info",
      data: audioContextRef.current.state,
    })
    if (audioContextRef.current.state === "running") {
      if (sourceRef.current) {
        sourceRef.current.stop()
      }
      sourceRef.current = audioContextRef.current.createBufferSource()
      sourceRef.current.buffer = audioBufferRef.current
      sourceRef.current.loop = true
      sourceRef.current.connect(audioContextRef.current.destination)
      sourceRef.current.start()
      debug("Sound playing", { level: "info" })
    } else {
      debug("AudioContext not running, sound blocked", { level: "info" })
    }
  }

  if (!isMounted) {
    return null
  }

  return (
    <Modal
      title="New Order"
      description="You have a new order!"
      open={open}
      onClose={() => setOpen(false)}
    >
      {isLoading ? (
        <p>Loading order details...</p>
      ) : error ? (
        <p className="text-destructive">Error: {error}</p>
      ) : orderData ? (
        <div>
          <p>
            Order ID: <span className="font-bold">{orderData?.orderId}</span>
          </p>
          <p>Email: {orderData?.email}</p>
          <p>
            Total Price:{" "}
            <span className="font-bold">
              {Config.currency}
              {orderData?.totalPrice?.toFixed(2)}
            </span>
          </p>
          {orderData?.appliedDiscountPercentage > 0 && (
            <p>
              Discount:{" "}
              <span className="font-bold">
                {orderData?.appliedDiscountPercentage}%
              </span>
            </p>
          )}
          <NewOrderModal order={orderData} setOpen={setOpen} />
        </div>
      ) : (
        <p>No order data available.</p>
      )}
    </Modal>
  )
}
